import React, { useState, useRef, useEffect } from "react";
import { Pagination, notification } from "antd";
import { useHistory, useLocation } from "react-router-dom";

import { scopeApiInstance } from "../../../dataProvider/axiosHelper";
import { Strings } from "../../../dataProvider/localize";
import { downloadDocument, getSerializedFilters } from "../../../utils/common";
import TableView from "./table/TableView";
import FiltersView from "./filters/FiltersView";
import ExportLimitModal from "./ExportLimitModal";

export default function FilterTableViewV2({ configData, refreshRef = { current: 1 }, initialFilters = {}, clearInitial, alterDataTable, customTableData = [] }) {

  const controller = useRef();
  const requestId = useRef(0);
  const exportController = useRef();
  const exportInProgress = useRef(false);
  const activeExport = useRef(null);
  const listingInProgress = useRef(false);
  const location = useLocation();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filterValues, setFilterValues] = useState({});
  const [tableData, setTableData] = useState(customTableData);
  const [totalRows, setTotalRows] = useState(0);
  const [lastLoadedSnapshot, setLastLoadedSnapshot] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportLimitModal, setShowExportLimitModal] = useState(false);
  const exportCsvConfig = configData && configData.exportCsv;
  const exportEnabled = !!(exportCsvConfig && exportCsvConfig.enabled);
  const exportOptions = exportEnabled && exportCsvConfig.options ? exportCsvConfig.options : [];
  const [refreshKey, setRefreshKey] = useState(Date.now()); // Used to signal refresh

  const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false);

  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState(null);

  const fetchTableData = async (pageNo, filters = {}) => {
    const currentRequestId = ++requestId.current;
    if (controller.current) {
      controller.current.abort();
    }
    setLoading(true);
    listingInProgress.current = true;
    setLastLoadedSnapshot(null);
    if (page !== pageNo) {
      setPage(pageNo)
    }
    let allFilters = {}
    if (((initialFilters && Object.keys(initialFilters).length > 0) || (configData && configData.initialFilters && Object.keys(configData.initialFilters).length > 0)) && !initialFiltersLoaded) {
      setFilterValues({ ...configData.initialFilters, ...initialFilters, });
      allFilters = { ...configData.initialFilters, ...initialFilters };
      setInitialFiltersLoaded(true);
      if (clearInitial) {
        clearInitial();
      }
    } else {
      setFilterValues({ ...filters });
      allFilters = { ...filters };
    }

    configData.filters.forEach(f => {
      if (f.submitModifier) {
        allFilters = f.submitModifier(allFilters)
      }
    })

    allFilters = await getSerializedFilters(allFilters);
    if (currentRequestId !== requestId.current) {
      return;
    }
    controller.current = new AbortController();
    let fetchParams = {};
    if (configData.fetchParams && configData.fetchParams.length > 0) {
      configData.fetchParams.forEach(param => {
        fetchParams[param.key] = param.value
      })
    }
    let fetchHeaders = {};
    if (configData.fetchHeaders && configData.fetchHeaders.length > 0) {
      configData.fetchHeaders.forEach(header => {
        fetchHeaders[header.key] = header.value
      })
    }
    const params = { page: pageNo, ...fetchParams, ...allFilters };
    return scopeApiInstance.get(
      configData.apiEndPoint,
      { params, signal: controller.current.signal, headers: fetchHeaders },
    ).then(res => {
      if (currentRequestId !== requestId.current) {
        return;
      }
      setLoading(false);
      listingInProgress.current = false;
      if (configData.dataTable.dataGetter) {
        let [data, count] = configData.dataTable.dataGetter(res.data && res.data.data ? res.data.data : {})
        setTableData(data);
        setTotalRows(count)
      } else {
        setTableData(res.data.data && res.data.data.rows ? res.data.data.rows : []);
        setTotalRows(res.data.data && res.data.data.count ? res.data.data.count : 0);
      }
      setLastLoadedSnapshot({ params, serializedFilters: allFilters, fetchParams });
      setRefreshKey(Date.now());
    }).catch((err) => {
      if (currentRequestId !== requestId.current) {
        return;
      }
      setLoading(false);
      listingInProgress.current = false;
      if (err) {
        notification.error({
          message: Strings.error_title,
          description: "There was an error while fetching Data.",
          className: "ant-error"
        })
      }
    })
  }

  const onFilter = (filters = filterValues, pageNo = page) => {
    if (configData && configData.apiEndPoint) {
      fetchTableData(pageNo, filters)
    }
  }

  const executeCsvExport = async (inEmail = false) => {
    const active = activeExport.current;
    if (!exportEnabled || !active || exportInProgress.current) {
      return;
    }
    const { config, payload } = active;
    exportInProgress.current = true;
    setExportLoading(true);
    const requestController = new AbortController();
    exportController.current = requestController;
    const params = { ...payload, ...(inEmail ? { in_email: true } : {}) };
    try {
      const method = (config.method || "get").toLowerCase();
      if (!config.apiEndPoint || !["get", "post", "put"].includes(method)) {
        throw new Error("Unsupported export method or missing endpoint configured.");
      }
      const requestConfig = { signal: requestController.signal, ...(config.headers ? { headers: config.headers } : {}) };
      const res = await (method === "get"
        ? scopeApiInstance.get(config.apiEndPoint, { ...requestConfig, params })
        : scopeApiInstance[method](config.apiEndPoint, params, requestConfig));
      if (requestController.signal.aborted) {
        return;
      }
      const response = res && res.data;
      if (!response) {
        throw new Error(Strings.generic_error);
      }
      if (config.checkResponseStatus && response.status !== 1) {
        throw response;
      }
      if (inEmail) {
        notification.success({
          message: Strings.success_title,
          description: config.emailSuccessMessage || "Your export will be sent to your email.",
          className: "ant-success"
        });
        return;
      }
      const data = response.data || response;
      const downloadData = config.downloadDataKey ? data[config.downloadDataKey] : data;
      const downloadUrl = downloadData && (config.downloadFileKey
        ? downloadData[config.downloadFileKey]
        : downloadData.uploaded_path || downloadData.csv_file_link || downloadData.csvFileLink || downloadData.xero_invoice_csv_file);
      if (downloadUrl) {
        await downloadDocument(downloadUrl);
      } else if (config.requireDownloadUrl) {
        throw new Error(Strings.generic_error);
      } else {
        notification.success({
          message: Strings.success_title,
          description: config.successMessage || "Export request completed.",
          className: "ant-success"
        });
      }
    } catch (err) {
      if (requestController.signal.aborted) {
        return;
      }
      const response = err && err.response;
      const errorData = response ? response.data : err;
      const accessDenied = response && (response.status === 401 || response.status === 403);
      const limitExceeded = errorData && ((errorData.data && errorData.data.limit_reached === true)
        || (config.isLimitExceeded && config.isLimitExceeded(errorData)));
      if (!inEmail && !accessDenied && limitExceeded) {
        setShowExportLimitModal(true);
        return;
      }
      notification.error({
        message: Strings.error_title,
        description: errorData && errorData.message ? errorData.message : Strings.generic_error,
        className: "ant-error"
      });
    } finally {
      exportInProgress.current = false;
      if (!requestController.signal.aborted) {
        setExportLoading(false);
      }
    }
  };

  const onExportCsv = (key) => {
    if (!exportEnabled || listingInProgress.current || loading || exportInProgress.current
      || showExportLimitModal || !lastLoadedSnapshot) {
      return;
    }
    if (!totalRows) {
      notification.error({
        message: "No records available to export.",
        className: "ant-error"
      });
      return;
    }
    const option = exportOptions.find(item => item.key === key);
    if ((exportOptions.length && !option) || (option && option.disabled)) {
      return;
    }
    const config = { ...exportCsvConfig, ...option };
    try {
      const payload = config.getPayload ? config.getPayload({
        filters: { ...lastLoadedSnapshot.serializedFilters },
        fetchParams: { ...lastLoadedSnapshot.fetchParams },
        totalRows,
        inEmail: false,
      }) : { ...lastLoadedSnapshot.params };
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
        throw new Error("Invalid export payload configured.");
      }
      const { page, limit, in_email, ...filters } = payload;
      activeExport.current = { config, payload: filters };
      if (Number.isFinite(config.limit) && config.limit > 0 && totalRows > config.limit) {
        setShowExportLimitModal(true);
        return;
      }
      executeCsvExport();
    } catch (err) {
      notification.error({
        message: Strings.error_title,
        description: err.message || Strings.generic_error,
        className: "ant-error"
      });
    }
  };

  const onCloseExportLimitModal = () => {
    if (!exportInProgress.current) {
      activeExport.current = null;
      setShowExportLimitModal(false);
    }
  };

  const onConfirmExportInEmail = async () => {
    if (!exportEnabled || !showExportLimitModal || !activeExport.current || exportInProgress.current) {
      return;
    }
    await executeCsvExport(true);
    if (exportController.current && !exportController.current.signal.aborted) {
      activeExport.current = null;
      setShowExportLimitModal(false);
    }
  };

  useEffect(() => () => {
    if (exportController.current) {
      exportController.current.abort();
    }
  }, []);

  useEffect(() => {
    if (configData && configData.apiEndPoint) {
      fetchTableData(1, location.searchParam && location.searchParam ? { [configData.searchableKey]: location.searchParam } : {});
    }
    return () => {
      requestId.current++;
      if (controller.current) {
        controller.current.abort()
      }
    };
  }, [configData, location.searchParam]);

  const onModalTrigger = (key, data) => {
    setModalData(data);
    setActiveModal(key);
  }

  const onDefaultClose = (reloadData = true) => {
    setActiveModal(null);
    setModalData(null);
    if (reloadData) {
      onFilter(filterValues, page);
    }
  }

  const activeExportConfig = activeExport.current ? activeExport.current.config : {};

  return (
    <div className="container-fluid px-0">
      {configData.headingView ? <configData.headingView onModalTrigger={onModalTrigger} routerHistory={history} /> : null}
      <div className="row">
        <div className="col-12 px-1">
          <FiltersView
            tableLoading={loading}
            onFilter={(v) => onFilter(v, 1)}
            initialFilters={configData.initialFilters ? configData.initialFilters : {}}
            filterValues={filterValues}
            onDefaultClose={onDefaultClose}
            onModalTrigger={onModalTrigger}
            setFilterValues={setFilterValues}
            filters={configData && configData.filters ? configData.filters : []}
            extraAction={exportEnabled ? {
              label: exportOptions.length ? "Export" : (exportCsvConfig.label || "Export CSV"),
              singleOptionLabel: exportOptions.length === 1 ? `Export ${exportOptions[0].label}` : null,
              options: exportOptions.length ? exportOptions : null,
              onSelect: onExportCsv,
              onClick: () => onExportCsv(),
              loading: exportLoading,
              disabled: loading || exportLoading || showExportLimitModal || !lastLoadedSnapshot
                || (!!exportCsvConfig.disableWhenNoRecords && !totalRows)
                || (exportOptions.length === 1 && !!exportOptions[0].disabled),
            } : null}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <TableView
            parentLoading={loading}
            refreshKey={refreshKey}
            onRefresh={() => onFilter(filterValues, page)}
            dataTable={alterDataTable ? alterDataTable : configData.dataTable}
            rowData={tableData} />
        </div>
      </div>
      <div className="row">
        <div className="col-12 d-flex justify-content-end pt-1">
          <Pagination
            disabled={!totalRows}
            className="cs-pagination-v2"
            total={totalRows}
            showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} ${configData.dataTable && configData.dataTable.pagination && configData.dataTable.pagination.text ? configData.dataTable.pagination.text : "rows"}`}
            onChange={(pageNo) => { fetchTableData(pageNo, filterValues) }}
            pageSize={configData.dataTable && configData.dataTable.pagination && configData.dataTable.pagination.pageSize ? configData.dataTable.pagination.pageSize : 10}
            current={page}
            defaultCurrent={1}
          />
        </div>
      </div>
      {configData.dataTable && configData.dataTable.modals ? configData.dataTable.modals.map(modal => <modal.modal key={modal.triggerKey} onDefaultClose={onDefaultClose} visible={modal.triggerKey === activeModal} rowData={modalData} />) : null}
      {exportEnabled ? <ExportLimitModal
        visible={showExportLimitModal}
        onClose={onCloseExportLimitModal}
        onConfirm={onConfirmExportInEmail}
        loading={exportLoading}
        title={activeExportConfig.limitModalTitle}
        message={activeExport.current ? (typeof activeExportConfig.limitModalMessage === "function"
          ? activeExportConfig.limitModalMessage(activeExportConfig.limit)
          : activeExportConfig.limitModalMessage || `This export contains more than ${activeExportConfig.limit} records. Large exports are sent by email. Would you like to continue?`) : ""}
        cancelText={activeExportConfig.limitModalCancelText}
        okText={activeExportConfig.limitModalOkText}
      /> : null}
    </div>
  )
}